/**
 * The Project page's tabs (PKG-PM-F, UX-PM-15, 01-roles.md section 9.6):
 * the PM's project setup, which replaced the Repos page. `fogbugz` is the
 * FogBugz ↔ ADO project link editor (epic PI-SPEC-SW-E308, feature
 * PI-SPEC-SW-1610).
 */
export const PROJECT_TABS = ["meeting-schedule", "product-areas", "repositories", "fogbugz"] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

/** The tab `/grooming/:project/project` opens, and the one an unknown tab falls back to. */
export const DEFAULT_PROJECT_TAB: ProjectTab = "meeting-schedule";

/** True when `value` is one of the Project page's tabs. */
export function isProjectTab(value: string | undefined): value is ProjectTab {
  return (PROJECT_TABS as readonly string[]).includes(value ?? "");
}

/**
 * Pages of one project in the /grooming route group (epics PI-SPEC-SW-E279
 * and PI-SPEC-SW-E280). `repos` is gone (PKG-PM-F): its URL only redirects
 * to the Project page.
 */
export type GroomingPage =
  | "backlog"
  | "session"
  | "one"
  | "project"
  | `project/${ProjectTab}`
  | `items/${string}`
  | `session/${string}`
  | `agenda/${string}`;

/** The path of a project's grooming page. */
export function groomingPath(project: string, page: GroomingPage): string {
  return `/grooming/${encodeURIComponent(project)}/${page}`;
}

/** The Project page, on `tab` when given (PKG-PM-F). */
export function projectPath(project: string, tab?: ProjectTab): string {
  return groomingPath(project, tab ? `project/${tab}` : "project");
}

/**
 * `/grooming/one` is the project-less One item page and `/grooming/queue` the
 * run queue (epic PI-SPEC-SW-E284), not projects called "one" or "queue".
 */
const NOT_A_PROJECT = new Set(["one", "queue"]);

/** The run queue: one per computer, across projects (epic PI-SPEC-SW-E284). */
export const GROOMING_QUEUE_PATH = "/grooming/queue";

/** The project in a `/grooming/:project/...` path, decoded; undefined elsewhere. */
export function projectFromPath(pathname: string): string | undefined {
  const match = /^\/grooming\/([^/]+)/.exec(pathname);
  if (!match?.[1]) return undefined;
  let project: string;
  try {
    project = decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
  return NOT_A_PROJECT.has(project) ? undefined : project;
}

/**
 * True on the shared agenda (`/grooming/:project/agenda/:batchId`): the one
 * grooming page any signed-in person may open, role or not (PKG-PM-C, epic
 * PI-SPEC-SW-E291), so the Toolbox frame shows a person with no role only
 * what their capabilities allow there.
 */
export function isSharedAgendaPath(pathname: string): boolean {
  return /^\/grooming\/[^/]+\/agenda\/[^/]+\/?$/.test(pathname);
}

/**
 * The grooming view a path shows, for the icon rail and the tabs. The legacy
 * `/repos` URL shows the Project view too (it redirects there).
 */
export type GroomingView = "backlog" | "session" | "one" | "queue" | "project";

export function viewFromPath(pathname: string): GroomingView | undefined {
  if (pathname === "/grooming/one") return "one";
  if (pathname === GROOMING_QUEUE_PATH) return "queue";
  const match = /^\/grooming\/[^/]+\/([^/]+)/.exec(pathname);
  switch (match?.[1]) {
    case "backlog":
    case "items":
      return "backlog";
    case "session":
    case "agenda":
      return "session";
    case "one":
      return "one";
    case "project":
    case "repos":
      return "project";
    default:
      return undefined;
  }
}
